import React, { useState, useEffect } from "react";
import { Clock, ExternalLink, CheckCircle2, ChevronLeft, ChevronRight } from "lucide-react";
import FeedItemRow from "../molecules/FeedItemRow";
import BureauCard from "../atoms/BureauCard";
import { INK, INK_3, LINE, BURGUNDY, BAD_BG, BAD_INK, OK_INK } from "../bureauTokens";

// Ligne squelette — mêmes proportions que FeedItemRow (grille 84px / 1fr /
// auto) pour ne pas décaler la mise en page une fois les données arrivées.
function FeedItemRowSkeleton() {
  return (
    <div
      className="grid items-center"
      style={{ gridTemplateColumns: "84px 1fr auto", gap: 14, padding: "10px 6px", borderTop: `1px solid ${LINE}` }}
    >
      <div className="h-4 w-12 rounded-full animate-pulse" style={{ background: LINE }} />
      <div className="min-w-0 space-y-1.5">
        <div className="h-2.5 w-28 rounded animate-pulse" style={{ background: LINE }} />
        <div className="h-2.5 w-40 rounded animate-pulse" style={{ background: LINE }} />
      </div>
      <div className="h-2.5 w-8 rounded animate-pulse justify-self-end" style={{ background: LINE }} />
    </div>
  );
}

// pageSize optionnel : au-delà, pagination locale (10 par page pour "Mes
// missions juridiques ponctuelles" par ex.) — sans pageSize, comportement
// inchangé (toute la liste, pas de pagination), pour les autres profils.
function PriorityFeedCard({ title, items, lateCount, onSeeAll, onOpenItem, onCompleteItem, loading, pageSize }) {
  const [page, setPage] = useState(1);

  // Repart à la page 1 dès que la liste change (rafraîchissement de
  // données, changement de profil) — évite de rester bloqué sur une page
  // devenue vide.
  useEffect(() => {
    setPage(1);
  }, [items]);

  const totalPages = pageSize ? Math.max(1, Math.ceil(items.length / pageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const pageItems = pageSize ? items.slice((currentPage - 1) * pageSize, currentPage * pageSize) : items;

  return (
    <BureauCard>
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs font-semibold" style={{ color: INK }}>
          <Clock size={14} style={{ color: BURGUNDY }} />
          {title}
          {lateCount > 0 && (
            <span
              className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: BAD_BG, color: BAD_INK }}
            >
              {lateCount} en retard
            </span>
          )}
        </div>
        <button
          onClick={onSeeAll}
          className="flex items-center gap-1 text-[11px] font-medium hover:opacity-80 transition-opacity"
          style={{ color: INK_3 }}
        >
          <ExternalLink size={11} /> Tout voir
        </button>
      </div>
      <div className="mt-1">
        {loading ? (
          Array.from({ length: pageSize || 4 }).map((_, i) => <FeedItemRowSkeleton key={i} />)
        ) : (
          <>
            {pageItems.map((item) => (
              <FeedItemRow key={item.id} item={item} onOpen={onOpenItem} onComplete={onCompleteItem} />
            ))}
            {items.length === 0 && (
              <div className="flex flex-col items-center gap-1.5 py-9 text-xs" style={{ color: INK_3 }}>
                <CheckCircle2 size={20} style={{ color: OK_INK }} />
                Rien à traiter — tout est à jour.
              </div>
            )}
          </>
        )}
      </div>
      {!loading && pageSize && totalPages > 1 && (
        <div className="flex items-center justify-between mt-2 pt-2" style={{ borderTop: `1px solid ${LINE}` }}>
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="flex items-center gap-1 text-[11px] font-medium disabled:opacity-30 hover:opacity-80 transition-opacity"
            style={{ color: INK_3 }}
          >
            <ChevronLeft size={13} /> Précédent
          </button>
          <span className="text-[11px]" style={{ color: INK_3 }}>
            Page {currentPage} / {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 text-[11px] font-medium disabled:opacity-30 hover:opacity-80 transition-opacity"
            style={{ color: INK_3 }}
          >
            Suivant <ChevronRight size={13} />
          </button>
        </div>
      )}
    </BureauCard>
  );
}

export default PriorityFeedCard;
